import React, { useEffect, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Coins, LockKeyhole, ArrowDownRight, ArrowUpRight, Plus, Unlock } from "lucide-react";

const MOVEMENT_LABEL = {
  opening_float: "Opening float",
  cash_in: "Cash in",
  cash_out: "Cash out",
  petty_cash: "Petty cash",
  cash_drop: "Cash drop",
  closing_count: "Blind count",
  sale_cash: "Cash sale",
};

export default function CashSessionPage() {
  const { branchId, hasPerm, user } = useAuth();
  const [state, setState] = useState({ open: false });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openingFloat, setOpeningFloat] = useState("5000");
  const [closing, setClosing] = useState(false);
  const [countInput, setCountInput] = useState("");
  const [varianceReason, setVarianceReason] = useState("");
  const [movementForm, setMovementForm] = useState({ type: "cash_in", amount: "", reason: "" });
  const [showMovement, setShowMovement] = useState(false);

  const load = async () => {
    if (!branchId) return;
    try {
      const [cur, hist] = await Promise.all([
        api.get("/cash-sessions/current", { params: { branch_id: branchId } }),
        api.get("/cash-sessions", { params: { branch_id: branchId, limit: 8 } }),
      ]);
      setState(cur.data);
      setHistory(hist.data);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [branchId]);

  const openSession = async () => {
    setBusy(true);
    try {
      await api.post("/cash-sessions/open", { branch_id: branchId, opening_float: parseFloat(openingFloat) || 0 });
      toast.success("Cash session opened");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addMovement = async (e) => {
    e.preventDefault();
    if (!state.session) return;
    try {
      await api.post(`/cash-sessions/${state.session.id}/movement`, {
        type: movementForm.type,
        amount: parseFloat(movementForm.amount),
        reason: movementForm.reason,
      });
      toast.success(`${MOVEMENT_LABEL[movementForm.type]} recorded`);
      setMovementForm({ type: "cash_in", amount: "", reason: "" });
      setShowMovement(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const closeSession = async () => {
    if (!state.session) return;
    setBusy(true);
    try {
      const res = await api.post(`/cash-sessions/${state.session.id}/close`, {
        actual_cash: parseFloat(countInput),
        variance_reason: varianceReason,
      });
      toast.success(`Closed · variance ${peso(res.data.variance)}`);
      setClosing(false); setCountInput(""); setVarianceReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const s = state.session;
  const expected = s?.expected_cash ?? 0;

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="cash-session-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Finance &amp; Cash</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Cash session</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Open a session at the start of the shift · record cash movements · perform a blind count to close the night. Variance is calculated server-side against every cash payment.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Status</div>
          <div className="mt-3 display-heading text-4xl ink">
            {state.open ? "Open" : "Closed"}
          </div>
          {s && (
            <div className="mt-1 tiny-label ink-subtle">
              since {new Date(s.opened_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })} · {s.opened_by_name}
            </div>
          )}
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first — cash sessions are per-branch.
        </div>
      )}

      {/* No open session */}
      {branchId && !state.open && (
        <div className="mt-14 max-w-md animate-fade-up animate-fade-up-1">
          <div className="section-eyebrow mb-4">Start the shift</div>
          <label className="block">
            <div className="tiny-label">Opening float (₱)</div>
            <input
              value={openingFloat}
              onChange={(e)=>setOpeningFloat(e.target.value)}
              type="number" step="0.01"
              data-testid="opening-float"
              className="mt-2 h-12 w-full rounded-md surface-card border hairline px-4 mono ink text-lg focus:border-foreground/50 outline-none"
            />
          </label>
          <button
            onClick={openSession}
            disabled={busy || !hasPerm("cash_session.manage")}
            data-testid="open-session-btn"
            className="mt-4 btn-pill btn-pill-solid h-11 w-full disabled:opacity-50"
          >
            <Unlock size={14}/> Open cash session
          </button>
        </div>
      )}

      {/* Open session */}
      {branchId && state.open && s && (
        <>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
            <Stat num=".01" label="Opening float"   value={peso(s.opening_float)}/>
            <Stat num=".02" label="Cash sales"      value={peso(s.cash_sales)}/>
            <Stat num=".03" label="Movements net"   value={peso(
              (s.movements || []).reduce((sum,m) => {
                if (["cash_in","petty_cash"].includes(m.type)) return sum + m.amount;
                if (["cash_out","cash_drop"].includes(m.type)) return sum - m.amount;
                return sum;
              }, 0)
            )}/>
            <Stat num=".04" label="Expected cash"   value={peso(expected)} accent/>
          </div>

          <div className="mt-14 flex items-center justify-between animate-fade-up animate-fade-up-2">
            <div className="section-eyebrow">Movements this shift</div>
            <div className="flex gap-2">
              <button onClick={()=>setShowMovement(true)} className="btn-pill btn-pill-outline h-9 text-[10px]" data-testid="add-movement-btn">
                <Plus size={12}/> Movement
              </button>
              <button onClick={()=>setClosing(true)} className="btn-pill btn-pill-solid h-9 text-[10px]" data-testid="close-session-btn">
                <LockKeyhole size={12}/> Close night
              </button>
            </div>
          </div>

          <div className="mt-6 divide-y hairline-soft border-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="movements-list">
            {(s.movements || []).slice().reverse().map((m, i) => (
              <div key={i} className="py-3 flex items-center gap-4">
                <div className="w-10 text-center">
                  {["cash_in","petty_cash","opening_float"].includes(m.type) ? <ArrowDownRight size={14} className="mx-auto ink"/> : <ArrowUpRight size={14} className="mx-auto ink-subtle"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink">{MOVEMENT_LABEL[m.type] || m.type}</div>
                  <div className="text-xs ink-subtle truncate mt-0.5">{m.reason || "—"}</div>
                </div>
                <div className="mono ink text-sm font-semibold">{peso(m.amount)}</div>
                <div className="text-[10px] ink-subtle mono w-20 text-right">{new Date(m.at).toLocaleTimeString("en-PH",{hour:"2-digit",minute:"2-digit"})}</div>
              </div>
            ))}
            {(s.movements || []).length === 0 && (
              <div className="py-6 text-center ink-subtle text-sm">No movements yet.</div>
            )}
          </div>
        </>
      )}

      {/* Recent sessions */}
      {branchId && history.length > 0 && (
        <div className="mt-16 animate-fade-up animate-fade-up-3">
          <div className="section-eyebrow mb-4">Recent shifts</div>
          <div className="divide-y hairline-soft border-y hairline-soft" data-testid="cash-history">
            {history.filter(h => h.status === "closed").slice(0, 6).map((h) => (
              <div key={h.id} className="py-3 flex items-center gap-6 text-sm">
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold ink w-32 truncate">
                  {new Date(h.opened_at).toLocaleDateString("en-PH", { day:"2-digit", month:"short" })}
                </div>
                <div className="mono ink-muted w-28">Exp {peso(h.expected_cash)}</div>
                <div className="mono ink-muted w-28">Act {peso(h.actual_cash)}</div>
                <div className={`mono w-28 font-semibold ${Math.abs(h.variance) < 0.01 ? "ink" : h.variance < 0 ? "text-red-500" : "text-emerald-500"}`}>
                  {h.variance > 0 ? "+" : ""}{peso(h.variance)}
                </div>
                <div className="flex-1 text-xs ink-subtle truncate italic">{h.variance_reason || "—"}</div>
                <div className="tiny-label">{h.closed_by_name?.split(" ")[0]}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Movement drawer */}
      {showMovement && (
        <Drawer onClose={()=>setShowMovement(false)} title="Record movement">
          <form onSubmit={addMovement} className="space-y-4">
            <label className="block">
              <div className="tiny-label">Type</div>
              <select value={movementForm.type} onChange={(e)=>setMovementForm({...movementForm, type: e.target.value})} className="mt-2 h-11 w-full rounded-md border hairline surface-card px-3 ink text-sm" data-testid="movement-type">
                <option value="cash_in">Cash in</option>
                <option value="cash_out">Cash out</option>
                <option value="petty_cash">Petty cash</option>
                <option value="cash_drop">Cash drop (to safe)</option>
              </select>
            </label>
            <label className="block">
              <div className="tiny-label">Amount (₱)</div>
              <input required type="number" step="0.01" value={movementForm.amount} onChange={(e)=>setMovementForm({...movementForm, amount: e.target.value})} className="mt-2 h-11 w-full rounded-md border hairline surface-card px-3 mono ink text-lg" data-testid="movement-amount"/>
            </label>
            <label className="block">
              <div className="tiny-label">Reason (required)</div>
              <input required value={movementForm.reason} onChange={(e)=>setMovementForm({...movementForm, reason: e.target.value})} className="mt-2 h-11 w-full rounded-md border hairline surface-card px-3 ink text-sm" data-testid="movement-reason" placeholder="e.g. supplier payment, staff advance"/>
            </label>
            <div className="pt-2 flex gap-2">
              <button type="button" onClick={()=>setShowMovement(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="movement-submit" className="btn-pill btn-pill-solid h-11 flex-1">Record</button>
            </div>
          </form>
        </Drawer>
      )}

      {/* Close drawer */}
      {closing && s && (
        <Drawer onClose={()=>setClosing(false)} title="Close the night">
          <div className="text-xs ink-muted mb-4">
            Count the drawer WITHOUT looking at the expected amount, then enter the total below. Variance requires a reason.
          </div>
          <label className="block">
            <div className="tiny-label">Actual cash counted (₱)</div>
            <input
              autoFocus
              type="number" step="0.01"
              value={countInput}
              onChange={(e)=>setCountInput(e.target.value)}
              data-testid="close-actual"
              className="mt-2 h-14 w-full rounded-md border hairline surface-card px-4 mono ink text-2xl font-semibold focus:border-foreground/50 outline-none"
              placeholder="0.00"
            />
          </label>
          {countInput && (
            <div className="mt-4 rounded-md border hairline surface-card p-4">
              <div className="text-xs ink-muted flex justify-between"><span>Expected</span><span className="mono ink">{peso(expected)}</span></div>
              <div className="text-xs ink-muted flex justify-between mt-2"><span>Counted</span><span className="mono ink">{peso(parseFloat(countInput) || 0)}</span></div>
              <div className="text-sm mt-3 pt-3 border-t hairline-soft flex justify-between font-semibold">
                <span className="ink">Variance</span>
                <span className={`mono ${Math.abs((parseFloat(countInput)||0) - expected) < 0.01 ? "ink" : (parseFloat(countInput)||0) < expected ? "text-red-500" : "text-emerald-500"}`}
                      data-testid="variance-display">
                  {((parseFloat(countInput)||0) - expected) > 0 ? "+" : ""}{peso((parseFloat(countInput)||0) - expected)}
                </span>
              </div>
            </div>
          )}
          {countInput && Math.abs((parseFloat(countInput)||0) - expected) > 0.01 && (
            <label className="block mt-4">
              <div className="tiny-label">Variance reason (required)</div>
              <textarea rows={2} value={varianceReason} onChange={(e)=>setVarianceReason(e.target.value)} data-testid="variance-reason" className="mt-2 w-full rounded-md border hairline surface-card px-3 py-2 ink text-sm"/>
            </label>
          )}
          <div className="mt-6 flex gap-2">
            <button onClick={()=>setClosing(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
            <button onClick={closeSession} disabled={busy || !countInput} data-testid="close-confirm" className="btn-pill btn-pill-solid h-11 flex-1 disabled:opacity-50">
              <LockKeyhole size={13}/> Lock the night
            </button>
          </div>
        </Drawer>
      )}
    </div>
  );
}

function Stat({ num, label, value, accent }) {
  return (
    <div data-testid={`cash-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 ink mono text-2xl font-semibold tracking-tight ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}

function Drawer({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
      <div className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
        <div className="section-eyebrow">Finance</div>
        <h2 className="mt-2 display-heading text-2xl ink">{title}</h2>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}
